import { Box, xcss } from '@atlaskit/primitives';
import {
  BitbucketTab,
  ConfluenceFilterContextProviderWithSearchConfig,
  ConfluenceSearchProviderWithSearchConfig,
  ConfluenceTab,
  JiraFilterContextProviderWithSearchConfig,
  JiraSearchProviderWithSearchConfig,
  JiraTab,
  MultiProductDialog,
  SearchConfigProvider,
  TownsquareTab,
  TrelloTab,
  UserInputContextProvider,
} from '@atlassian/product-search-dialog';
import React, { ReactNode, useRef, useState } from 'react';
// Disabling this import because the dep only provides a href
// but this href it generated from our backend so we need to assume it's safe
// eslint-disable-next-line no-restricted-imports, @townsquare/rules/no-restricted-imports
import { Link, useRouter } from 'react-resource-router';

import { getConfig } from '@townsquare/config';
import { PRODUCT_IDENTIFIER } from '@townsquare/config/constants';
import { useUserStore } from '@townsquare/user-store';
import { useWorkspaceStore } from '@townsquare/workspace-store';
import { useCPUSScope } from '@townsquare/workspace-store/hooks';

export interface ClientProviderConfig {
  accountId: string;
  aggregatorUrl: string;
  cloudId: string;
  collaborationGraphUrl: string;
  isUserAnonymous: boolean;
}

export const ClientProvider: React.FC<{
  children: (clientProviderConfig: ClientProviderConfig) => JSX.Element;
}> = ({ children }) => {
  const config = getConfig();
  const [workspace] = useWorkspaceStore();
  const [{ accountId: userId }] = useUserStore();
  const [{ cloudId }] = useWorkspaceStore();

  // baseUrl doesn't seem to actually be used. No need to block search if a cloudUrl can't be found
  // Providing cloudUrl as best effort
  const url = workspace.cloudUrl ? `http://${workspace.cloudUrl}` : '';

  const clientProviderConfig: ClientProviderConfig = {
    isUserAnonymous: !userId,
    aggregatorUrl: config.searchUrl,
    collaborationGraphUrl: config.collabGraphUrl,
    cloudId,
    accountId: userId,
  };

  return (
    <>
      <SearchConfigProvider>
        <ConfluenceSearchProviderWithSearchConfig
          config={{
            ...clientProviderConfig,
            baseUrl: url,
            useCollaborationGraphForRecents: true,
            useRecentWork: true,
          }}
        >
          <JiraSearchProviderWithSearchConfig {...clientProviderConfig} baseUrl={url}>
            {children(clientProviderConfig)}
          </JiraSearchProviderWithSearchConfig>
        </ConfluenceSearchProviderWithSearchConfig>
      </SearchConfigProvider>
    </>
  );
};

const TOWNSQUARE_SCOPES = [
  'townsquare.project',
  'townsquare.goal',
  'townsquare.learning',
  'townsquare.help-pointer',
  'townsquare.tag',
  'cpus.user',
];

type LinkComponentProps = {
  children?: ReactNode;
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  className?: string;
  isKeyboardHighlighted?: boolean;
  isCollapsed?: boolean;
};

const searchWrapperStyles = xcss({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  minWidth: '1px',
  width: '100%',
  maxWidth: '780px',
});

const LinkComponent = ({
  linkProps,
  setIsExpanded,
}: {
  linkProps: LinkComponentProps;
  setIsExpanded: (isExpanded: boolean) => void;
}) => {
  return (
    <Link
      {...linkProps}
      onClick={e => {
        if (e.metaKey) {
          // Opening in a new tab, keep search dialog open
          return;
        }
        setIsExpanded(false);
      }}
    >
      {linkProps.children}
    </Link>
  );
};

export const SearchDialog = () => {
  const [, routerActions] = useRouter();

  const onNavigate = (url: string) => routerActions.push(url);
  const onNavigateSecondary = (href: string) => {
    window.location.assign(href);
  };

  const [isOpen, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const [{ cloudId, organisationId: orgId, name: workspaceName, globalId: workspaceId }] = useWorkspaceStore();
  const [user] = useUserStore();

  const cpusScope = useCPUSScope({ isInviteRelated: false });

  const onNavigateMultiProduct = (id: string, href: string) => {
    const navigationCallback = id === PRODUCT_IDENTIFIER ? onNavigate : onNavigateSecondary;

    navigationCallback(href);
  };

  const workspaces = [
    {
      name: workspaceName,
      id: cloudId,
      baseUrl: '/',
    },
  ];

  return (
    <UserInputContextProvider stickySearchEnabled isExpanded={isOpen}>
      <JiraFilterContextProviderWithSearchConfig isEnabled={isOpen}>
        <ConfluenceFilterContextProviderWithSearchConfig isEnabled={isOpen}>
          <ClientProvider>
            {clientProviderConfig => (
              <Box xcss={searchWrapperStyles} testId="innermost-before-search">
                <MultiProductDialog
                  shouldFillContainer={true}
                  aggregatorUrl={clientProviderConfig.aggregatorUrl}
                  abTestCloudId={cloudId}
                  isExpanded={isOpen}
                  setIsExpanded={setOpen}
                  user={user}
                  forwardRef={ref}
                  onNavigateGeneric={onNavigateMultiProduct}
                  keepExpandedInTab
                  dialogFeatures={{
                    interactiveSkeleton: {
                      // We don't have a generic search url ATM so disabled for now
                      // @ts-expect-error - These props arent defined in the types but are used in the consuming package code.
                      enabled: false,
                      advancedSearchUrl: '/search',
                      placeholder: 'Search',
                    },
                  }}
                >
                  {({ onRetry }) => (
                    <>
                      <TownsquareTab
                        order={0}
                        onRetry={onRetry}
                        permissionSupplier={() => Promise.resolve(TOWNSQUARE_SCOPES)}
                        workspaces={workspaces}
                        userTeamConfig={{
                          context: {
                            containerId: workspaceId,
                            contextType: 'filter',
                            principalId: 'Context',
                            productKey: cpusScope,
                            siteId: cloudId,
                            organizationId: orgId,
                          },
                        }}
                        linkComponent={linkProps => <LinkComponent {...{ linkProps, setIsExpanded: setOpen }} />}
                      />
                      <ConfluenceTab
                        order={1}
                        isExpanded={isOpen}
                        onRetry={onRetry}
                        isPrefetchingEnabled={false}
                        features={undefined}
                      />
                      <JiraTab
                        order={2}
                        onNavigate={onNavigateSecondary}
                        isExpanded={isOpen}
                        onRetry={onRetry}
                        features={undefined}
                      />
                      <TrelloTab order={3} onRetry={onRetry} />
                      <BitbucketTab
                        order={4}
                        onRetry={onRetry}
                        features={{
                          enableSingleScopesCall: true,
                        }}
                      />
                    </>
                  )}
                </MultiProductDialog>
              </Box>
            )}
          </ClientProvider>
        </ConfluenceFilterContextProviderWithSearchConfig>
      </JiraFilterContextProviderWithSearchConfig>
    </UserInputContextProvider>
  );
};
